import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tag, Form, Typography, Button } from 'antd';
import moment from 'moment';
import { useQuery } from 'react-query';
import { FilePdfTwoTone, MailTwoTone } from '@ant-design/icons';
import DataGrid from 'components/DataGrid';
import RouterLink from 'components/RouterLink';
import ParListSelect from 'components/ParListSelect';
import DF from '../../DisplayFramework';
import {
  findCIByAnyRefOrStatusOrDate,
  FindCIByAnyRefOrStatusOrDateParams,
  CIListResponse,
} from '../../services/clientInvoice';
import { toCurrency } from '../../utils/utils';
import TasksListPopover from '../workflow/TasksListPopover';
import NewButton from 'components/NewButton';
import { API_URLS } from 'utils/config';
import { downloadBlobFile, indexBy } from 'utils/utils2';
import GetLabel from 'DisplayFramework/components/GetLabel';
import IActions from 'icons/IActions';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import ModalControllerNV from 'components/ModalControllerNV';
import CIDetail from './CIDetail';

export type CIListSearchProps = {
  isModal?: string;
  onSelect?: (item: CIListResponse) => void;
  hideCIListModal?: () => void;
  hideCol?: boolean;
  hideExtraAction?: boolean;
  ctreference?: string;
  hideFilters?: boolean;
  hideContractCol?:boolean;
};

const CIList = ({
  onSelect,
  isModal,
  hideCIListModal,
  hideCol,
  hideExtraAction,
  ctreference,
  hideFilters,
  hideContractCol
}: CIListSearchProps) => {
  const [form] = Form.useForm();
  const fieldName = 'CINVOICE';
  const PRINTING_URL = API_URLS.PRINTING;
  const [isFirst, setIsFirst] = useState<boolean>(false);
  const getUrl = (printid: number) =>
    `${PRINTING_URL}/api/print/download?printid=${printid}`;

  const initialSearchParams: FindCIByAnyRefOrStatusOrDateParams = {
    cidocreference: '',
    cireference: '',
    cistatus: '',
    citype: '',
    clientname: '',
    ctreference: ctreference ?? '',
    datemax: '',
    datemin: '',
    mgid: Number(localStorage.getItem('mgid')),
    paystatus: '',
    tpreference: '',
    currcode: '',
  };

  const [searchParams, setSearchParams] =
    useState<FindCIByAnyRefOrStatusOrDateParams>(initialSearchParams);

  const {
    data: clientInvoices,
    isFetching,
    refetch,
  } = useQuery(
    ['findCIByAnyRefOrStatusOrDate', searchParams],
    () =>
      findCIByAnyRefOrStatusOrDate(searchParams
      ).then((res) => res.list || []),
    { enabled: isFirst || ctreference !== undefined }
  );

  useEffect(() => {
    if (clientInvoices) {
      form.setFieldsValue({ [fieldName]: clientInvoices });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, clientInvoices]);

  const getName = useCallback(
    (name: string) => (record: CIListResponse) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions, 'ciid')[record.ciid];

      return [fieldName, index, name];
    },
    [form]
  );
  const columns = useMemo<DataGridColDef<CIListResponse>[]>(
    () => [
      {
        dataIndex: 'cireference',
        filtrable: true,
        render: (cireference, record) =>
          onSelect ? (
            <Typography.Link onClick={() => onSelect(record)}>
              {cireference}
            </Typography.Link>
          ) : !hideFilters ? (
            <RouterLink to={`/client-invoice/${record.ciid}`}>
              {cireference}
            </RouterLink>
          ) : (
            <ModalControllerNV
              modal={({ setVisible, visible }) => (
                <CIDetail
                  ciid={record.ciid}
                  onCancel={() => setVisible(false)}
                  showAsModal
                  visible={visible}
                  showButtons={false}
                />
              )}
              content={({ setVisible, visible }) => (
                <a onClick={() => setVisible(true)}>{record.cireference}</a>
              )}
            />
          ),
        fixed: 'left',
        width: '20%',
      },
      {
        dataIndex: 'cidocdate',
        width: '15%',
        render: (cidocdate) => moment(cidocdate).format('YYYY-MM-DD'),
      },
      {
        dataIndex: 'cirefcreditnote',
        width: '20%',
      },
      {
        dataIndex: 'citypelabel',
        filtrable: 'citype',
        filterComponent: (
          <ParListSelect parcode="CITYPE" style={{ width: '100%' }} />
        ),
        width: '20%',
      },

      {
        dataIndex: 'ctreference',
        filtrable: true,
        width: '15%',
      },

      {
        dataIndex: 'clientname',
        filtrable: true,
        width: '23%',
      },
      {
        filterComponent: <ParListSelect parcode="CURRENCY" />,
        filtrable: 'currcode',
        dataIndex: 'cigrosstotal',
        key: 'cigrosstotal',
        align: 'right',
        width: '15%',
        render: (value, record) => toCurrency(value, record.currcode),
      },
      {
        dataIndex: 'cidueamount',
        align: 'right',
        width: '15%',
        fixed: 'right',
        render: (value, record) => toCurrency(value, record.currcode),
      },
      {
        dataIndex: 'printid',
        fixed: 'right',
        width: '10%',
        align: 'center',
        render: (printid, record) => {
          return (
            printid && (
              <Button
                size="small"
                onClick={(ev) => {
                  ev.preventDefault();
                  downloadBlobFile(
                    record.cireference + '.pdf',
                    getUrl(record.printid)
                  );
                }}
                icon={<FilePdfTwoTone twoToneColor="blue" />}
              >
                {' '}
                <GetLabel label="invoice" />
              </Button>
            )
          );
        },
      },
      {
        dataIndex: 'printidemail',
        fixed: 'right',
        width: '10%',
        align: 'center',
        render: (printidemail, record) => {
          return (
            printidemail && (
              <Button
                size="small"
                icon={
                  <MailTwoTone
                    twoToneColor="green"
                    onClick={(ev) => {
                      ev.preventDefault();
                      downloadBlobFile(
                        record.cireference + '_email',
                        getUrl(record.printidemail)
                      );
                    }}
                  />
                }
              >
                {' '}
                <GetLabel label="email" />
              </Button>
            )
          );
        },
      },
      {
        comcode: 'cistatuslabel',
        dataIndex: 'cistatus',
        filtrable: 'cistatus',
        filterComponent: (
          <ParListSelect parcode="CISTATUS" style={{ width: '100%' }} />
        ),
        render: (cistatuslabel, record) => (
          <span>
            {record.cistatus.split(',').map((tag) => {
              let color;
              if (tag === 'INIT') {
                color = 'blue';
              }
              if (tag === 'VALID') {
                color = 'green';
              }
              if (tag === 'BAP') {
                color = 'volcano';
              }
              if (tag === 'CANCEL') {
                color = 'geekblue';
              }
              if (tag === 'PAID') {
                color = 'blue';
              }

              if (tag === 'INPROG') {
                color = 'warning';
              }

              if (tag === 'REJECT') {
                color = 'red';
              }

              if (tag === 'PART') {
                color = 'purple';
              }

              return (
                <Tag
                  icon={
                    record.cistatus === 'VALID' ? (
                      <CheckCircleOutlined />
                    ) : null || record.cistatus === 'INPROG' ? (
                      <SyncOutlined spin />
                    ) : null
                  }
                  color={color}
                  key={tag}
                >
                  {record.cistatuslabel}
                </Tag>
              );
            })}
          </span>
        ),
        width: 150,
      },

      {
        comcode: 'actions',
        key: 'actions',
        fixed: 'right',
        width: 100,
        render: (_, record) => (
          <TasksListPopover
            entityId={record.ciid}
            entityName="CINVOICE"
            entityReference={record.cireference}
            title={record.cireference + ' - ' + record.clientname}
            onClose={refetch}
          >
            <Button size="small" icon={<IActions height="15px" width="15px" />}>
              {' '}
              <GetLabel label="actions" />
            </Button>
          </TasksListPopover>
        ),
      },
    ],

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getName, form, refetch]
  );

  return (
    <DF.Provider pagcode="cilist">
      <DataGrid
        columns={
          !hideCol
            ? !hideContractCol
              ? columns
              : columns.filter(
                (e) =>
                e.key !== 'actions'
              )
            : columns.filter(
                (ea) =>
                  ea.key !== 'actions' &&
                  ea.dataIndex !== 'printidemail' &&
                  ea.dataIndex !== 'printid'
              )
           
        }
        pagination={{ pageSize: 50 }}
        scroll={{ x: 1000, y: 430 }}
        dataSource={clientInvoices}
        componentName="clientInvoice_list"
        initialSearchValues={initialSearchParams}
        onSearch={(values) => {
          setIsFirst(true);

          setSearchParams({
            ...values,
            ctreference: values.ctreference ?? ctreference,
          });
          refetch();
        }}
        hideFilters={hideFilters ? true : false}
        isLoading={isFetching}
        titleComcode="List of Client Invoices"
        rowKey={(record) => record.ciid}
        hideExtraAction={hideExtraAction}
        extraActions={
          <NewButton comcode="header.btnnew" to="/client-invoice/create" />
        }
      />
    </DF.Provider>
  );
};

export default CIList;
